/**
 * PRIVA Governance — Client Master.
 *
 * Top of the Multi-Client hierarchy: WarehouseX Business Clients.
 *   Business Client → SAP System → SAP Connection → Signal Services → PRIVA
 *
 * The internal `clientId` slug is generated automatically from the Business
 * Client Name and is NEVER entered by admins. It is surfaced only as a small
 * muted "internal id" in the detail dialog — business names everywhere else.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Loader2, RefreshCw, ShieldCheck, Plus, Pencil, Trash2, Upload, X,
} from "lucide-react";
import {
  listSmartewmClients, createSmartewmClient, updateSmartewmClient,
  deleteSmartewmClient,
} from "../lib/adminApi";
import { ClientLogo } from "../components/EnvBadge";

function extractError(e, fallback = "Request failed.") {
  const msg = e?.response?.data?.detail || e?.message || fallback;
  return typeof msg === "string" ? msg : fallback;
}

const emptyForm = {
  clientId: null,
  clientName: "",
  clientCode: "",
  description: "",
  logo: "",
  status: "Active",
};

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function ClientMasterPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    listSmartewmClients()
      .then((data) => setRows(data.clients || []))
      .catch((e) => setError(extractError(e, "Failed to load clients.")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(emptyForm);
    setEditing(false);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      clientId: row.clientId,
      clientName: row.clientName || "",
      clientCode: row.clientCode || "",
      description: row.description || "",
      logo: row.logo || "",
      status: row.status || "Active",
      _createdBy: row.createdBy,
      _modifiedBy: row.modifiedBy,
      _createdAt: row.createdAt,
      _updatedAt: row.updatedAt,
    });
    setEditing(true);
    setDialogOpen(true);
  };

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onLogoFile = (file) => {
    if (!file) return;
    const okTypes = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"];
    if (!okTypes.includes(file.type)) {
      toast.error("Logo must be a PNG, JPG or SVG file.");
      return;
    }
    if (file.size > 512 * 1024) {
      toast.error("Logo must be under 512 KB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setField("logo")(reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.clientName.trim()) {
      toast.error("Business Client Name is required.");
      return;
    }
    if (!form.clientCode.trim()) {
      toast.error("Client Code is required (e.g. PS4).");
      return;
    }
    setSaving(true);
    const payload = {
      clientName: form.clientName.trim(),
      clientCode: form.clientCode.trim(),
      description: form.description.trim(),
      logo: form.logo || "",
      status: form.status,
    };
    try {
      if (editing) {
        await updateSmartewmClient(form.clientId, payload);
        toast.success("Client updated.");
      } else {
        await createSmartewmClient(payload);
        toast.success("Client created.");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(extractError(e, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (row) => {
    if (!window.confirm(`Delete client "${row.clientName}"? This cannot be undone.`)) return;
    try {
      await deleteSmartewmClient(row.clientId);
      toast.success("Client deleted.");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  return (
    <div className="p-6 max-w-[1280px] mx-auto" data-testid="client-master-page">
      <header className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <ShieldCheck className="w-4 h-4 text-[#025979]" />
            <p className="text-[10px] uppercase tracking-widest text-[#025979] font-semibold">PRIVA Governance</p>
          </div>
          <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo, sans-serif" }}>Client Master</h1>
          <p className="text-xs text-[#6B7280] mt-1 max-w-2xl">
            WarehouseX Business Clients. Each client owns one or more SAP Systems.
            The internal client id is generated automatically from the name.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <button type="button" onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#025979] border border-[#025979] rounded-sm hover:bg-[#025979]/5" data-testid="client-master-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <Button onClick={openCreate} className="h-8 bg-[#025979] hover:bg-[#03738C] text-xs" data-testid="client-master-create-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New Client
          </Button>
        </div>
      </header>

      {error && <div className="text-red-600 text-xs py-2" data-testid="client-master-error">{error}</div>}

      <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-x-auto">
        <table className="min-w-full text-xs" data-testid="client-master-table">
          <thead className="bg-[#F8FAFC] text-[10px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <Th>Business Client</Th><Th>Client Code</Th><Th>Status</Th>
              <Th>Description</Th><Th>Modified By</Th><Th>Modified</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {loading ? (
              <tr><td colSpan={7} className="text-center py-12 text-[#6B7280]"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-[#6B7280]" data-testid="client-master-empty">No clients yet. Create one to get started.</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.clientId} className="hover:bg-[#F8FAFC]/70" data-testid={`client-master-row-${r.clientId}`}>
                  <Td className="font-medium text-[#111827]">
                    <span className="inline-flex items-center gap-2"><ClientLogo logo={r.logo} name={r.clientName} size={22} />{r.clientName}</span>
                  </Td>
                  <Td className="font-mono text-[11px]">{r.clientCode || "—"}</Td>
                  <Td>
                    <Badge className={r.status === "Active"
                      ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                      : "bg-gray-100 text-gray-600 border border-gray-200"}>
                      {r.status === "Active" ? "Active" : "Inactive"}
                    </Badge>
                  </Td>
                  <Td className="text-[#374151] max-w-[280px] truncate">{r.description || <span className="text-[#9CA3AF]">—</span>}</Td>
                  <Td className="text-[#374151]">{r.modifiedBy || "—"}</Td>
                  <Td className="text-[11px] text-[#6B7280] whitespace-nowrap">{fmtTime(r.updatedAt)}</Td>
                  <Td>
                    <div className="flex items-center gap-1.5">
                      <IconBtn title="Edit" onClick={() => openEdit(r)} testId={`client-master-edit-${r.clientId}`}><Pencil className="w-3.5 h-3.5" /></IconBtn>
                      <IconBtn title="Delete" onClick={() => doDelete(r)} testId={`client-master-delete-${r.clientId}`} danger><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="client-master-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Business Client" : "New Business Client"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Field label="Business Client Name *">
              <Input value={form.clientName} onChange={(e) => setField("clientName")(e.target.value)} placeholder="PrimeS4" className="h-8 text-xs" data-testid="client-master-form-name" />
            </Field>
            <Field label="Client Code *" hint="Used in SAP System identifiers (e.g. PS4-EWM-DEV).">
              <Input value={form.clientCode} onChange={(e) => setField("clientCode")(e.target.value.toUpperCase())} placeholder="PS4" className="h-8 text-xs font-mono" data-testid="client-master-form-code" />
            </Field>
            <Field label="Description">
              <Textarea value={form.description} onChange={(e) => setField("description")(e.target.value)} rows={2} className="text-xs" data-testid="client-master-form-description" />
            </Field>
            <div className="flex flex-col gap-1.5">
              <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">Client Logo</span>
              <div className="flex items-center gap-3">
                <ClientLogo logo={form.logo} name={form.clientName} size={44} className="shrink-0" />
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-[#025979] border border-[#025979] rounded-sm hover:bg-[#025979]/5 cursor-pointer" data-testid="client-master-form-logo-upload">
                    <Upload className="w-3.5 h-3.5" /> Upload
                    <input type="file" accept="image/png,image/jpeg,image/svg+xml" className="hidden" onChange={(e) => onLogoFile(e.target.files?.[0])} />
                  </label>
                  {form.logo && (
                    <button type="button" onClick={() => setField("logo")("")} className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-red-600 border border-red-200 rounded-sm hover:bg-red-50" data-testid="client-master-form-logo-remove">
                      <X className="w-3.5 h-3.5" /> Remove
                    </button>
                  )}
                </div>
              </div>
              <span className="text-[10px] text-[#9CA3AF]">PNG, JPG or SVG · under 512 KB.</span>
            </div>
            <div className="flex items-center justify-between gap-2 pt-1">
              <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">Status — {form.status === "Active" ? "Active" : "Inactive"}</span>
              <Switch checked={form.status === "Active"} onCheckedChange={(v) => setField("status")(v ? "Active" : "Disabled")} data-testid="client-master-form-status" />
            </div>
            {editing && (
              <div className="pt-2 border-t border-[#F1F5F9] text-[10px] text-[#9CA3AF] space-y-0.5">
                <p>Internal id: <span className="font-mono text-[#6B7280]">{form.clientId}</span></p>
                <p>Created by {form._createdBy || "—"} · {fmtTime(form._createdAt)}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="h-8 text-xs" data-testid="client-master-form-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="h-8 bg-[#025979] hover:bg-[#03738C] text-xs" data-testid="client-master-form-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (editing ? "Save Changes" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Th({ children }) { return <th className="text-left font-medium px-3 py-2 whitespace-nowrap">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>; }
function Field({ label, hint, children }) {
  return (
    <label className="flex flex-col gap-1 text-[10px] uppercase tracking-wider text-[#6B7280]">
      {label}{children}
      {hint && <span className="text-[10px] normal-case tracking-normal text-[#9CA3AF]">{hint}</span>}
    </label>
  );
}
function IconBtn({ children, onClick, title, testId, danger }) {
  return (
    <button type="button" title={title} onClick={onClick} data-testid={testId}
      className={`p-1.5 rounded-sm border transition-colors ${danger ? "border-red-200 text-red-600 hover:bg-red-50" : "border-[#E5E7EB] text-[#374151] hover:bg-[#F8FAFC]"}`}>
      {children}
    </button>
  );
}
